import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { Toaster } from '@/components/ui/toaster';
import { TooltipProvider } from '@/components/ui/tooltip';
import NotFound from '@/pages/not-found';
import { Route, Switch, Router as WouterRouter, useLocation } from 'wouter';
import { useEffect } from 'react';
import { I18nProvider } from '@/contexts/I18nContext';
import { Layout } from '@/components/layout';
import { ConsentBanner } from '@/components/ConsentBanner';
import { trackPageView } from '@/lib/analytics';

// Pages
import HomePage from '@/pages/home';
import ExplorePage from '@/pages/explore';
import ListingDetailPage from '@/pages/listing-detail';
import CategoriesPage from '@/pages/categories';
import ForBusinessesPage from '@/pages/for-businesses';
import ListYourBusinessPage from '@/pages/list-your-business';
import AdminPage from '@/pages/admin';
import MerchantPortalPage from '@/pages/merchant-portal';
import JoinPage from '@/pages/join';
import PrivacyPolicyPage from '@/pages/privacy';
import MerchantDetailPage from '@/pages/merchant-detail';
import CompaniesPage from '@/pages/companies';
import DealsPage from '@/pages/deals';
import AboutPage from '@/pages/about';
import FaqPage from '@/pages/faq';
import GuidePage from '@/pages/guide';
import BrandLabPage from '@/pages/brand-lab';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

function Router() {
  return (
    <Switch>
      <Route path="/" component={HomePage} />
      <Route path="/explore" component={ExplorePage} />
      <Route path="/companies" component={CompaniesPage} />
      <Route path="/deals" component={DealsPage} />
      <Route path="/about" component={AboutPage} />
      <Route path="/faq" component={FaqPage} />
      <Route path="/guide" component={GuidePage} />
      <Route path="/listings/:slug" component={ListingDetailPage} />
      <Route path="/merchants/:slug" component={MerchantDetailPage} />
      <Route path="/categories" component={CategoriesPage} />
      <Route path="/for-businesses" component={ForBusinessesPage} />
      <Route path="/list-your-business" component={ListYourBusinessPage} />
      <Route path="/admin" component={AdminPage} />
      <Route path="/merchant-portal" component={MerchantPortalPage} />
      <Route path="/join/:token" component={JoinPage} />
      <Route path="/privacy" component={PrivacyPolicyPage} />
      <Route path="/brand-lab" component={BrandLabPage} />
      <Route component={NotFound} />
    </Switch>
  );
}

const EXCLUDED_TRACK_PATHS = ['/admin', '/merchant-portal'];

function ScrollToTop() {
  const [location] = useLocation();
  useEffect(() => {
    if (!location.startsWith('/explore')) window.scrollTo({ top: 0, behavior: 'instant' });
    // Track page view on every route change, excluding admin routes
    if (!EXCLUDED_TRACK_PATHS.some((p) => location === p || location.startsWith(p + '/'))) {
      trackPageView(location);
    }
  }, [location]);
  return null;
}

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <I18nProvider>
        <TooltipProvider>
          <WouterRouter base={import.meta.env.BASE_URL.replace(/\/$/, '')}>
            <Layout>
              <ScrollToTop />
              <Router />
            </Layout>
            <ConsentBanner />
          </WouterRouter>
          <Toaster />
        </TooltipProvider>
      </I18nProvider>
    </QueryClientProvider>
  );
}

export default App;
